<script setup>
import DeletePackageAction from "@frontJs/components/actions/projectManagement/apikom/DeletePackageAction.vue";
import { router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import WysiwygField from "@js/components/fields/WysiwygField.vue";
import SpinnerIcon from "@js/components/icons/SpinnerIcon.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { goBack, useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { number, string, object } from "yup";

const { t } = useI18n();

const themes = [
  { value: "seo", label: t("label.seo") },
  { value: "sea", label: t("label.sea") },
  { value: "smo", label: t("label.smo") },
  { value: "sma", label: t("label.sma") },
  { value: "sem", label: t("label.sem") },
  { value: "dev", label: t("label.dev") },
  { value: "hosting", label: t("label.hosting") },
  { value: "outsourcing", label: t("label.outsourcing") },
  { value: "cloudflare", label: t("label.cloudflare") },
  { value: "emailing", label: t("label.emailing") },
  { value: "shoppingfeed", label: t("label.shoppingfeed") },
  { value: "performance_stage", label: t("label.performanceStage") },
  {
    value: "performance_percentage",
    label: t("label.performancePercentage"),
  },
  { value: "special", label: t("label.special") },
];

const frequencies = [
  { value: "monthly", label: t("label.monthly") },
  { value: "quarterly", label: t("label.quarterly") },
  { value: "yearly", label: t("label.yearly") },
];

const props = defineProps({
  package: {
    required: false,
    type: Object,
  },
});

const { route } = useRoute();

const form = reactive({
  theme: props.package
    ? themes.find((theme) => theme.value === props.package.theme)
    : themes[0],
  name: props.package ? props.package.name : "",
  description: props.package ? props.package.description : "",
  requirements: props.package ? props.package.requirements : "",
  price: props.package ? props.package.price : "",
  frequency:
    props.package && props.package.frequency
      ? frequencies.find(
          (frequency) => frequency.value === props.package.frequency.value,
        )
      : null,
  hoursPerMonth: props.package ? props.package.hoursPerMonth : "",
  netlinkingBudgetPerMonth:
    props.package && props.package.netlinkingBudgetPerMonth
      ? props.package.netlinkingBudgetPerMonth
      : null,
  minCost: props.package ? props.package.minCost : "",
  maxCost: props.package ? props.package.maxCost : "",
});

const isSubmitting = ref(false);

const selectedTab = ref(0);

const validationSchema = object({
  theme: object().required(t("error.required")),
  name: string().required(t("error.required")),
  description: string().nullable(),
  requirements: string().nullable(),
  price: number()
    .typeError(t("error.number"))
    .when("theme", {
      is: (theme) =>
        theme === "seo" ||
        theme === "sea" ||
        theme === "smo" ||
        theme === "sma" ||
        theme === "sem" ||
        theme === "hosting" ||
        theme === "outsourcing" ||
        theme === "cloudflare" ||
        theme === "emailing" ||
        theme === "shoppingfeed" ||
        theme === "special",
      then: number()
        .typeError(t("error.number"))
        .required(t("error.required"))
        .min(0),
    }),
  frequency: object().nullable(),
  hoursPerMonth: number()
    .typeError(t("error.number"))
    .min(0)
    .when("theme", {
      is: (theme) =>
        theme === "seo" ||
        theme === "sea" ||
        theme === "smo" ||
        theme === "sma",
      then: number()
        .typeError(t("error.number"))
        .required(t("error.required"))
        .min(0),
    }),
  netlinkingBudgetPerMonth: number()
    .typeError(t("error.number"))
    .when("theme", {
      is: "seo",
      then: number()
        .typeError(t("error.number"))
        .required(t("error.required"))
        .min(0),
    }),
  minCost: number()
    .typeError(t("error.number"))
    .when("theme", {
      is: (theme) => theme === "sea" || theme === "sma",
      then: number()
        .typeError(t("error.number"))
        .required(t("error.required"))
        .min(0),
    }),
  maxCost: number()
    .typeError(t("error.number"))
    .when("theme", {
      is: (theme) => theme === "sea" || theme === "sma",
      then: number()
        .typeError(t("error.number"))
        .required(t("error.required"))
        .min(0),
    }),
});

function changeTab(index) {
  selectedTab.value = index;
}

function savePackage() {
  isSubmitting.value = true;

  let error;

  fetch(
    props.package
      ? route("front.projectManagement.apikom.postPackage", {
          package: props.package.id,
        })
      : route("front.projectManagement.apikom.postPackage"),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        theme: form.theme.value,
        name: form.name,
        description: form.description,
        requirements: form.requirements,
        price: form.price,
        frequency: form.frequency ? form.frequency.value : null,
        hoursPerMonth: form.hoursPerMonth,
        netlinkingBudgetPerMonth: form.netlinkingBudgetPerMonth,
        minCost: form.minCost,
        maxCost: form.maxCost,
      }),
    },
  )
    .then((response) => {
      error = !response.ok;

      return response.json();
    })
    .then((data) => {
      if (error) {
        toast.error(data.message, { position: "top-right" });
      } else {
        toast.success(t("toast.successfullySaved"), {
          position: "top-right",
        });

        if (props.package) {
          router.visit(
            route("front.projectManagement.apikom.updatePackage", {
              package: data.id,
            }),
          );
        } else {
          goBack();
        }
      }
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
      router.visit(route("front.projectManagement.apikom.packages"));
    });
}

function deleteCallback() {
  toast.success(t("toast.successfullyDeleted"), { position: "top-right" });
  router.visit(route("front.projectManagement.apikom.packages"), {
    preserveState: true,
    replace: true,
  });
}
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    @submit="savePackage"
    v-slot="{ errors }"
  >
    <TabGroup
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{
        information: t('title.information'),
      }"
    >
      <template #panels>
        <TabPanel>
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <ListboxField
              v-model="form.theme"
              field-id="theme"
              field-name="theme"
              :label="t('label.theme')"
              :options="themes"
              required
            >
              <template #buttonContent>
                {{ form.theme.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>
          </div>

          <InputField
            v-model="form.name"
            :label="t('label.name')"
            :placeholder="t('label.name')"
            field-name="name"
            field-id="name"
            type="text"
            required
          />

          <WysiwygField
            v-model="form.description"
            :label="t('label.description')"
            field-name="description"
            field-id="description"
          />

          <WysiwygField
            v-model="form.requirements"
            :label="t('label.requirements')"
            field-name="requirements"
            field-id="requirements"
          />

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-if="
                form.theme.value === 'seo' ||
                form.theme.value === 'sea' ||
                form.theme.value === 'smo' ||
                form.theme.value === 'sma' ||
                form.theme.value === 'sem' ||
                form.theme.value === 'dev' ||
                form.theme.value === 'hosting' ||
                form.theme.value === 'outsourcing' ||
                form.theme.value === 'cloudflare' ||
                form.theme.value === 'emailing' ||
                form.theme.value === 'shoppingfeed' ||
                form.theme.value === 'special'
              "
              v-model="form.price"
              :label="t('label.price')"
              :placeholder="t('label.price')"
              field-name="price"
              field-id="price"
              type="number"
              step="0.01"
              min="0"
              required
            />
          </div>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <ListboxField
              v-model="form.frequency"
              field-id="frequency"
              field-name="frequency"
              :label="t('label.frequency')"
              :options="frequencies"
              has-empty-option
            >
              <template #buttonContent>
                {{ form.frequency.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>
          </div>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-if="
                form.theme.value === 'seo' ||
                form.theme.value === 'sea' ||
                form.theme.value === 'smo' ||
                form.theme.value === 'sma'
              "
              v-model="form.hoursPerMonth"
              :label="t('label.hoursPerMonth')"
              :placeholder="t('label.hoursPerMonth')"
              field-name="hoursPerMonth"
              field-id="hoursPerMonth"
              type="number"
              step="1"
              min="0"
              required
            />

            <InputField
              v-if="form.theme.value === 'seo'"
              v-model="form.netlinkingBudgetPerMonth"
              :label="t('label.netlinkingBudgetPerMonth')"
              :placeholder="t('label.netlinkingBudgetPerMonth')"
              field-name="netlinkingBudgetPerMonth"
              field-id="netlinkingBudgetPerMonth"
              type="number"
              step="0.01"
              min="0"
              required
            />
          </div>

          <div
            v-if="form.theme.value === 'sea' || form.theme.value === 'sma'"
            class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4"
          >
            <InputField
              v-model="form.minCost"
              :label="t('label.minCost')"
              :placeholder="t('label.minCost')"
              field-name="minCost"
              field-id="minCost"
              type="number"
              step="0.01"
              min="0"
              required
            />

            <InputField
              v-model="form.maxCost"
              :label="t('label.maxCost')"
              :placeholder="t('label.maxCost')"
              field-name="maxCost"
              field-id="maxCost"
              type="number"
              step="0.01"
              min="0"
              required
            />
          </div>
        </TabPanel>
      </template>
    </TabGroup>

    <div
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton type="transparent" :callback="() => goBack()">
        {{ t("button.back") }}
      </ActionButton>

      <DeletePackageAction
        v-if="props.package"
        :package="props.package"
        :callback="deleteCallback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton type="secondary" :callback="() => setIsOpen(true)">
            {{ t("button.delete") }}
          </ActionButton>
        </template>
      </DeletePackageAction>

      <ActionButton
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.save") }}
        </template>

        <template v-else>
          <SpinnerIcon
            class="mr-1 size-4 animate-spin fill-white text-gray-200"
          />
          {{ t("button.saveInProgress") }}
        </template>
      </ActionButton>

      <div v-if="Object.keys(errors).length > 0" class="text-sm text-red-500">
        {{ t("error.formHasErrors") }}
      </div>
    </div>
  </Form>
</template>
